import {
  LeftOutlined,
  LockFilled,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Image, Spin } from 'antd';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { MessageStates } from 'containers/MessageBox';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useDispatch, useSelector } from 'react-redux';
import IconDownload from 'shared/assets/images/icons/IconDownload.svg';
import IconPrint from 'shared/assets/images/icons/IconPrint.svg';
import { ReactComponent as IconPlus } from 'shared/assets/images/icons/iconPlus.svg';
import { turnMessageOn } from 'store/app/actions';
import { AdvancedUserRightsCodes, MenuSize } from 'store/app/enums';
import { getMenuSize } from 'store/app/selectors';
import { getAuthUserLicence } from 'store/auth/selectors';
import { MessageData } from 'store/app/types';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { isSmallOrMediumPlanLocked } from 'utils/subscriptionPlanGate';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import {
  TRANSACTION_ATTACHMENT_ACCEPT,
  validateTransactionAttachment,
} from 'utils/safe-upload';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import {
  AttachmentIconButton,
  AttachmentViewerWrapper,
  StyledIconClose,
  StyledIconNext,
  StyledIconPrevious,
} from './styles';
import { AttachmentViewerProps } from './types';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

/** Render PDF wider then scale down via CSS `zoom` (Chromium / Safari): clearer text than raw width at 100%. */
const PDF_RASTER_BASELINE_SHARPNESS = 1.42;
const PDF_RASTER_ZOOM_QUALITY_SLOPE = 0.4;
const PDF_RASTER_QUALITY_MAX_BOOST = 2.85;
/** Ignore sub-pixel / scrollbar width jitter so react-pdf is not torn down and reloaded. */
const CONTAINER_WIDTH_QUANTIZE_PX = 4;
const CONTAINER_WIDTH_UPDATE_THRESHOLD_PX = 8;

function quantizeContainerWidth(width: number): number {
  return Math.max(
    0,
    Math.round(width / CONTAINER_WIDTH_QUANTIZE_PX) *
      CONTAINER_WIDTH_QUANTIZE_PX
  );
}

export const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  onClose,
  transactionReference = '[Transaction reference goes here]',
  attachments = [],
  attachmentLoading,
  providerName,
  attachmentSourceUrl,
  drawerOffset,
  zIndex,
  embedded,
  standaloneWidth,
  standaloneFullHeight,
  centered,
  showUpload = true,
  onUploadAttachment,
  currentSubscription,
  onUpgradeModalOpen,
  currentUser,
  currentOrganisation,
  hasAdvancedUserRights,
  topSlot,
}) => {
  const { t } = useTranslation();
  /** Edit drawer: content-sized preview, max drawer height, scroll when content is tall. */
  const expandedEmbeddedLayout = Boolean(embedded && standaloneFullHeight);
  const pwaMobileNav = usePwaMobileNavEnabled();
  const compactFit = expandedEmbeddedLayout && pwaMobileNav;
  /** PWA review bottom sheet: short preview strip; tap opens full-screen lightbox. */
  const pwaSheetCollapsed = Boolean(
    embedded && pwaMobileNav && !standaloneFullHeight
  );
  const fitPreviewInPanel = compactFit || pwaSheetCollapsed;
  const tapToExpandPreview = fitPreviewInPanel;
  // PWA review sheet already has Close in the chrome — don't also show a leave control in the photo.
  const hideEmbeddedClose = Boolean(embedded && pwaMobileNav);
  // Standalone transaction attachment overlay on phone: always expose Back
  // (the close X is easy to lose off-screen / hard to see).
  const showPwaBackButton = Boolean(pwaMobileNav && !embedded);
  // History (and other PWA sheets) portal to document.body at z-index 1250.
  // A fixed viewer inside the page stays under that sheet and keeps the
  // desktop centered size. Mount the phone overlay on body instead.
  const mountPwaOverlay = (node: React.ReactElement) => {
    if (showPwaBackButton && typeof document !== 'undefined') {
      return createPortal(
        <div
          className="av-viewer-pwa-scrim"
          onClick={onClose}
          role="presentation"
        >
          <div
            className="av-viewer-pwa-scrim__card"
            onClick={(event) => event.stopPropagation()}
          >
            {node}
          </div>
        </div>,
        document.body
      );
    }
    return node;
  };
  useOverlayBackHandler(
    showPwaBackButton,
    onClose,
    'attachment-viewer-standalone',
    pwaMobileNav
  );
  const dispatch = useDispatch();
  const menuSize = useSelector(getMenuSize);
  const userLicence = useSelector(getAuthUserLicence);
  const menuWidth = menuSize === MenuSize.WIDE ? 212 : 88;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [imageRotation, setImageRotation] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  useOverlayBackHandler(
    lightboxOpen,
    () => setLightboxOpen(false),
    'attachment-viewer-lightbox',
    pwaMobileNav
  );
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [scaledImageSize, setScaledImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const previewAreaRef = useRef<HTMLDivElement>(null);
  const viewerWrapperRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const measureContainerRafRef = useRef<number | null>(null);

  const isUploadLocked = isSmallOrMediumPlanLocked(
    currentSubscription,
    userLicence
  );

  const isCreateSubmissionDisabled =
    hasAdvancedUserRights &&
    !!currentOrganisation &&
    !hasAdvancedUserRight(
      currentUser,
      currentOrganisation,
      AdvancedUserRightsCodes.CREATE_SUBMISSION
    );

  useEffect(() => {
    if (currentIndex >= attachments.length) {
      setCurrentIndex(0);
    }
  }, [attachments.length, attachments, currentIndex]);

  /** Rows / attachments can reuse the same array reference — include txn ref + attachment id so zoom resets when opening another file */
  const attachmentOpenContextKey = `${transactionReference ?? ''}|${
    attachments[currentIndex]?.attachmentID ?? ''
  }|${currentIndex}`;

  useEffect(() => {
    setImageRotation(0);
    setPreviewZoom(1);
    setNumPages(null);
    setLightboxOpen(false);
  }, [previewUrl, attachmentOpenContextKey]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const measureContainer = useCallback(() => {
    const el = previewAreaRef.current;
    if (!el) return;
    const style = window.getComputedStyle(el);
    const paddingX =
      parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const paddingY =
      parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const nextWidth = quantizeContainerWidth(
      Math.max(0, el.clientWidth - paddingX)
    );
    const nextHeight = Math.max(0, Math.round(el.clientHeight - paddingY));
    setContainerWidth((prev) => {
      if (prev === nextWidth) return prev;
      if (prev === 0) return nextWidth;
      if (Math.abs(prev - nextWidth) < CONTAINER_WIDTH_UPDATE_THRESHOLD_PX) {
        return prev;
      }
      return nextWidth;
    });
    setContainerHeight((prev) => {
      if (prev === nextHeight) return prev;
      if (Math.abs(prev - nextHeight) < 4) {
        return prev;
      }
      return nextHeight;
    });
  }, []);

  const scheduleMeasureContainer = useCallback(() => {
    if (measureContainerRafRef.current != null) return;
    measureContainerRafRef.current = requestAnimationFrame(() => {
      measureContainerRafRef.current = null;
      measureContainer();
    });
  }, [measureContainer]);

  useLayoutEffect(() => {
    scheduleMeasureContainer();
  }, [
    standaloneWidth,
    drawerOffset,
    embedded,
    standaloneFullHeight,
    scheduleMeasureContainer,
  ]);

  useEffect(() => {
    scheduleMeasureContainer();
    window.addEventListener('resize', scheduleMeasureContainer);
    return () => {
      window.removeEventListener('resize', scheduleMeasureContainer);
      if (measureContainerRafRef.current != null) {
        cancelAnimationFrame(measureContainerRafRef.current);
        measureContainerRafRef.current = null;
      }
    };
  }, [scheduleMeasureContainer]);

  useEffect(() => {
    const el = previewAreaRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      scheduleMeasureContainer();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scheduleMeasureContainer, attachmentLoading, attachments.length]);

  const currentAttachmentForMeasure = attachments[currentIndex];
  const isPdfFile = currentAttachmentForMeasure?.fileName
    ?.toLowerCase()
    .endsWith('.pdf');

  const currentAttachmentForPreview = attachments[currentIndex];
  const currentAttachmentPreviewKey = `${
    currentAttachmentForPreview?.attachmentID ?? ''
  }|${currentIndex}|${currentAttachmentForPreview?.fileName ?? ''}`;

  useEffect(() => {
    // Create object URL for the current attachment
    if (!currentAttachmentForPreview?.fileContent) {
      setPreviewUrl(null);
      return;
    }

    const fileName = currentAttachmentForPreview.fileName || '';
    const ext = fileName.split('.').pop()?.toLowerCase();
    let mimeType = '';
    switch (ext) {
      case 'jpg':
      case 'jpeg':
        mimeType = 'image/jpeg';
        break;
      case 'png':
        mimeType = 'image/png';
        break;
      case 'gif':
        mimeType = 'image/gif';
        break;
      case 'webp':
        mimeType = 'image/webp';
        break;
      case 'pdf':
        mimeType = 'application/pdf';
        break;
      default:
        mimeType = '';
    }

    const fileContent = currentAttachmentForPreview.fileContent;
    let uint8Array: Uint8Array;
    if (typeof fileContent === 'string') {
      const base64ToUint8Array = (base64: string) => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      uint8Array = base64ToUint8Array(fileContent);
    } else {
      console.warn('Unsupported fileContent type');
      setPreviewUrl(null);
      return;
    }

    const blob = mimeType //@ts-ignore
      ? new Blob([uint8Array], { type: mimeType }) //@ts-ignore
      : new Blob([uint8Array]);
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);

    // Cleanup the object URL when the component unmounts or the attachment changes
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [currentAttachmentPreviewKey, currentAttachmentForPreview?.fileContent]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : attachments.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < attachments.length - 1 ? prev + 1 : 0));
  };

  const handleDownload = () => {
    if (previewUrl && attachments[currentIndex]?.fileName) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.setAttribute('download', attachments[currentIndex].fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (!previewUrl) {
      alert(t('vat-report.could-not-get-document-for-printing'));
      return;
    }

    const printWindow = window.open(previewUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 500);
      };
    } else {
      alert(t('vat-report.could-not-open-print-window'));
    }
  };

  const handleRotateClockwise = () => {
    setPreviewZoom(1);
    setImageRotation((prev) => (prev + 90) % 360);
  };

  const handleRotateCounterClockwise = () => {
    setPreviewZoom(1);
    setImageRotation((prev) => (prev - 90 + 360) % 360);
  };

  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = () => {
    setPreviewZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setPreviewZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  };

  // Recalculate image scale when rotation or container size changes
  useEffect(() => {
    if (!imageDimensions) return;
    const layoutWidth =
      containerWidth > 0
        ? containerWidth
        : previewAreaRef.current?.getBoundingClientRect().width ?? 0;
    if (layoutWidth <= 0) return;

    const layoutHeight =
      containerHeight > 0
        ? containerHeight
        : previewAreaRef.current?.clientHeight ?? 0;
    const isRotated = imageRotation === 90 || imageRotation === 270;
    const visualNatW = isRotated
      ? imageDimensions.height
      : imageDimensions.width;
    const visualNatH = isRotated
      ? imageDimensions.width
      : imageDimensions.height;

    let visualW: number;
    let visualH: number;
    if (fitPreviewInPanel) {
      if (layoutHeight <= 0) return;
      // Always contain — full image visible in the strip / compact panel.
      const scale = Math.min(
        layoutWidth / visualNatW,
        layoutHeight / visualNatH
      );
      visualW = visualNatW * scale;
      visualH = visualNatH * scale;
    } else if (isRotated) {
      // Width-fit; height follows aspect (scroll if taller than panel).
      visualW = layoutWidth;
      visualH = visualW * (visualNatH / visualNatW);
      if (layoutHeight > 0 && visualH > layoutHeight) {
        const scale = layoutHeight / visualH;
        visualW *= scale;
        visualH *= scale;
      }
    } else {
      visualW = layoutWidth;
      visualH = visualW / (imageDimensions.width / imageDimensions.height);
      if (layoutHeight > 0 && visualH > layoutHeight) {
        const scale = layoutHeight / visualH;
        visualW *= scale;
        visualH *= scale;
      }
    }

    // Store pre-rotation image box. Outer layout swaps these when rotated.
    if (isRotated) {
      setScaledImageSize({ width: visualH, height: visualW });
    } else {
      setScaledImageSize({ width: visualW, height: visualH });
    }
  }, [
    imageRotation,
    imageDimensions,
    containerWidth,
    containerHeight,
    fitPreviewInPanel,
  ]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setLightboxOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxOpen]);

  // Reset image dimensions when attachment changes
  useEffect(() => {
    setImageDimensions(null);
    setScaledImageSize(null);
  }, [currentIndex]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onUploadAttachment) {
      const validation = validateTransactionAttachment(file);
      if (!validation.ok) {
        dispatch(
          turnMessageOn({
            title: t('common.unsupported-file-type-title'),
            description: validation.message,
            type: MessageStates.WARNING,
          })
        );
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      // Check if we're at the attachment limit
      if (attachments.length >= 10) {
        const message: MessageData = {
          title: t('vat-report.attachment-limit-reached'),
          description:
            t('vat-report.attachment-limit-description'),
          type: MessageStates.WARNING,
        };
        dispatch(turnMessageOn(message));
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      onUploadAttachment(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    if (isCreateSubmissionDisabled && onUpgradeModalOpen) {
      onUpgradeModalOpen();
      return;
    }

    // Check if we're at the attachment limit before opening file dialog
    if (attachments.length >= 10) {
      const message: MessageData = {
          title: t('vat-report.attachment-limit-reached'),
        description:
          t('vat-report.attachment-limit-description'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    fileInputRef.current?.click();
  };

  const providerLogo = (() => {
    if (!providerName?.trim()) return null;
    const img = (
      <ProviderIcon
        providerName={providerName}
        alt={providerName}
        width={25}
        style={{ marginTop: 7, display: 'block' }}
      />
    );
    const url = attachmentSourceUrl?.trim();
    if (url) {
      return (
        <a
          href={url}
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          target="_blank"
          rel="noreferrer"
        >
          {img}
        </a>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {img}
      </span>
    );
  })();

  const pwaBackButton = showPwaBackButton ? (
    <AttachmentIconButton
      type="button"
      className="av-back-button"
      onClick={onClose}
      aria-label={t('common.back')}
    >
      <LeftOutlined aria-hidden />
      <span>{t('common.back')}</span>
    </AttachmentIconButton>
  ) : null;

  if (attachmentLoading) {
    return mountPwaOverlay(
      <AttachmentViewerWrapper
        ref={viewerWrapperRef}
        $menuWidth={menuWidth}
        $drawerOffset={drawerOffset}
        $zIndex={zIndex}
        $embedded={embedded}
        $standaloneWidth={standaloneWidth}
        $standaloneFullHeight={standaloneFullHeight}
        $centered={centered}
        $pwaSheet={pwaSheetCollapsed}
        className={
          showPwaBackButton
            ? 'av-viewer--pwa-standalone'
            : pwaSheetCollapsed
              ? 'av-viewer--pwa-sheet-collapsed'
              : undefined
        }
        style={{ alignItems: 'stretch', justifyContent: 'flex-start' }}
      >
        <div
          className="av-top-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div className="av-top-bar-left av-file-info">
            {pwaBackButton}
            {providerLogo}
          </div>
          <div className="av-top-bar-right">
            {topSlot ?? (
              <div className="av-previewing-text">
                {t('vat-report.previewing-attachment')}
              </div>
            )}
            {!hideEmbeddedClose && !showPwaBackButton ? (
              <AttachmentIconButton
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
              >
                <StyledIconClose aria-hidden />
              </AttachmentIconButton>
            ) : null}
          </div>
        </div>
        {pwaSheetCollapsed ? (
          <div
            className="av-preview-area av-preview-area--loading"
            aria-busy
            aria-live="polite"
          >
            <Spin size="small" />
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: 200,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <Spin size="large" />
          </div>
        )}
      </AttachmentViewerWrapper>
    );
  }

  if (!attachments || attachments.length === 0) {
    // No attachment UI
    return mountPwaOverlay(
      <AttachmentViewerWrapper
        ref={viewerWrapperRef}
        $menuWidth={menuWidth}
        $drawerOffset={drawerOffset}
        $zIndex={zIndex}
        $embedded={embedded}
        $standaloneWidth={standaloneWidth}
        $standaloneFullHeight={standaloneFullHeight}
        $centered={centered}
        $pwaSheet={pwaSheetCollapsed}
        className={showPwaBackButton ? 'av-viewer--pwa-standalone' : undefined}
      >
        {!hideEmbeddedClose && !showPwaBackButton ? (
          <div style={{ position: 'absolute', top: '20px', right: '24px' }}>
            <AttachmentIconButton
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <StyledIconClose aria-hidden />
            </AttachmentIconButton>
          </div>
        ) : null}
        {showPwaBackButton ? (
          <div className="av-top-bar" style={{ flexShrink: 0 }}>
            <div className="av-top-bar-left av-file-info">{pwaBackButton}</div>
          </div>
        ) : null}
        <div className="av-centered-content">
          <div className="av-transaction-reference">{transactionReference}</div>
          <div className="av-no-attachment-text">
            {t('vat-report.you-have-no-linked-attachments')}
          </div>
          {showUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept={TRANSACTION_ATTACHMENT_ACCEPT}
              />
              <StyledTooltip
                title={
                  isCreateSubmissionDisabled
                    ? t('vat-report.upload-attachment-no-permission')
                    : ''
                }
              >
                <span>
                  <ScopeButton
                    type="primary"
                    width="fit-content"
                    height="fit-content"
                    fontSize="16px"
                    style={{
                      borderRadius: 20,
                      fontWeight: 600,
                      border: 'none',
                      width: 'fit-content',
                      height: 'fit-content',
                    }}
                    onClick={handleUploadClick}
                    disabled={attachmentLoading || isCreateSubmissionDisabled}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        fontFamily: 'Inter',
                        fontWeight: 600,
                        fontSize: 12,
                        lineHeight: '24px',
                        letterSpacing: 0,
                      }}
                    >
                      {isUploadLocked ? (
                        <LockFilled style={{ fontSize: 12 }} />
                      ) : (
                        <IconPlus style={{ fontSize: 12 }} />
                      )}
                      {t('vat-report.upload-attachment')}
                    </div>
                  </ScopeButton>
                </span>
              </StyledTooltip>
            </>
          )}
        </div>
      </AttachmentViewerWrapper>
    );
  }

  const currentAttachment = attachments[currentIndex];
  const fileExtension = currentAttachment?.fileName
    ?.split('.')
    .pop()
    ?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(
    fileExtension || ''
  );
  const isPdf = fileExtension === 'pdf';

  // Attachment present UI
  return mountPwaOverlay(
    <AttachmentViewerWrapper
      ref={viewerWrapperRef}
      $menuWidth={menuWidth}
      $drawerOffset={drawerOffset}
      $zIndex={zIndex}
      $embedded={embedded}
      $standaloneWidth={standaloneWidth}
      $standaloneFullHeight={standaloneFullHeight}
      $centered={centered}
      $compactFit={compactFit}
      $pwaSheet={pwaSheetCollapsed}
      className={
        compactFit
          ? 'av-viewer--compact'
          : pwaSheetCollapsed
            ? 'av-viewer--pwa-sheet-collapsed'
            : showPwaBackButton
              ? 'av-viewer--pwa-standalone'
              : undefined
      }
      style={{ alignItems: 'stretch', justifyContent: 'flex-start' }}
    >
      <div
        className="av-top-bar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="av-top-bar-left av-file-info">
          {pwaBackButton}
          {providerLogo}
          <StyledTooltip title={currentAttachment?.fileName || t('vat-report.no-file-name')}>
            <span
              style={{
                maxWidth: showPwaBackButton ? '120px' : '90px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {currentAttachment?.fileName || t('vat-report.no-file-name')}
            </span>
          </StyledTooltip>
        </div>

        <div className="av-top-bar-right">
          {topSlot ?? (
            <div className="av-previewing-text">
              {t('vat-report.previewing-x-of-y', {
                current: currentIndex + 1,
                total: attachments.length,
              })}
            </div>
          )}
          {attachments.length > 1 ? (
            <>
              <StyledTooltip title={t('vat-report.previous-attachment')}>
                <span>
                  <AttachmentIconButton
                    type="button"
                    onClick={handlePrevious}
                    aria-label={t('vat-report.previous-attachment')}
                  >
                    <StyledIconPrevious aria-hidden />
                  </AttachmentIconButton>
                </span>
              </StyledTooltip>
              <StyledTooltip title={t('vat-report.next-attachment')}>
                <span>
                  <AttachmentIconButton
                    type="button"
                    onClick={handleNext}
                    aria-label={t('vat-report.next-attachment')}
                  >
                    <StyledIconNext aria-hidden />
                  </AttachmentIconButton>
                </span>
              </StyledTooltip>
            </>
          ) : null}
          {!hideEmbeddedClose && !showPwaBackButton ? (
            <AttachmentIconButton
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <StyledIconClose aria-hidden />
            </AttachmentIconButton>
          ) : null}
        </div>
      </div>
      <div className="av-toolbar">
        {showUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept={TRANSACTION_ATTACHMENT_ACCEPT}
            />
            <StyledTooltip
              title={
                isCreateSubmissionDisabled
                  ? t('vat-report.upload-attachment-no-permission')
                  : ''
              }
            >
              <span>
                <ScopeButton
                  width="fit-content"
                  height="fit-content"
                  type="primary"
                  style={{ width: 'fit-content', height: 'fit-content' }}
                  bg_color="var(--color-primary, #41aaaf)"
                  onClick={handleUploadClick}
                  disabled={attachmentLoading || isCreateSubmissionDisabled}
                >
                  <div className="av-upload-button-content">
                    {isUploadLocked ? (
                      <LockFilled style={{ fontSize: 12 }} />
                    ) : (
                      <IconPlus style={{ fontSize: 12 }} />
                    )}
                    <span>{t('vat-report.upload-attachment')}</span>
                  </div>
                </ScopeButton>
              </span>
            </StyledTooltip>
          </>
        )}
        <div className="av-toolbar-icon-group">
          {(isImage || isPdf) && (
            <>
              <StyledTooltip title={t('vat-report.zoom-out')}>
                <span
                  className="av-toolbar-icon"
                  onClick={previewZoom > ZOOM_MIN ? handleZoomOut : undefined}
                  style={{
                    opacity: previewZoom <= ZOOM_MIN ? 0.5 : 1,
                    cursor: previewZoom <= ZOOM_MIN ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ZoomOutOutlined style={{ fontSize: 18, color: '#fff' }} />
                </span>
              </StyledTooltip>
              <StyledTooltip title={t('vat-report.zoom-in')}>
                <span
                  className="av-toolbar-icon"
                  onClick={previewZoom < ZOOM_MAX ? handleZoomIn : undefined}
                  style={{
                    opacity: previewZoom >= ZOOM_MAX ? 0.5 : 1,
                    cursor: previewZoom >= ZOOM_MAX ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ZoomInOutlined style={{ fontSize: 18, color: '#fff' }} />
                </span>
              </StyledTooltip>
            </>
          )}
          {isImage && (
            <>
              <StyledTooltip title={t('vat-report.rotate-clockwise')}>
                <span
                  className="av-toolbar-icon"
                  onClick={handleRotateClockwise}
                >
                  <RotateRightOutlined
                    style={{ fontSize: 18, color: '#fff' }}
                  />
                </span>
              </StyledTooltip>
              <StyledTooltip title={t('vat-report.rotate-counter-clockwise')}>
                <span
                  className="av-toolbar-icon"
                  onClick={handleRotateCounterClockwise}
                >
                  <RotateLeftOutlined style={{ fontSize: 18, color: '#fff' }} />
                </span>
              </StyledTooltip>
            </>
          )}
        </div>
        <div className="av-toolbar-icon-group">
          {/* <span className="av-toolbar-icon">
            <img
              src={IconTrashcan}
              alt="Delete"
              className="av-toolbar-svg av-toolbar-svg-trash"
            />
          </span> */}
          <StyledTooltip title={t('vat-report.download-file')}>
            <span className="av-toolbar-icon" onClick={handleDownload}>
              <img
                src={IconDownload}
                alt={t('vat-report.download-file')}
                className="av-toolbar-svg"
              />
            </span>
          </StyledTooltip>
          <StyledTooltip title={t('vat-report.print-file')}>
            <span className="av-toolbar-icon" onClick={handlePrint}>
              <img src={IconPrint} alt={t('vat-report.print-file')} className="av-toolbar-svg" />
            </span>
          </StyledTooltip>
        </div>
      </div>
      <div
        className="av-preview-area"
        data-page-label={
          pwaSheetCollapsed
            ? t('vat-report.page-x-of-y', {
                defaultValue: 'Page {{current}} of {{total}}',
                current: 1,
                total: Math.max(1, numPages || 1),
              })
            : undefined
        }
      >
        {currentAttachment && (
          <div ref={previewAreaRef} className="av-pdf-container">
            {isImage && previewUrl ? (
              <div
                className={
                  fitPreviewInPanel
                    ? 'av-image-fit-wrap av-image-fit-wrap--compact'
                    : undefined
                }
                style={{
                  display: fitPreviewInPanel ? 'flex' : 'block',
                  width: '100%',
                  ...(fitPreviewInPanel
                    ? {
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }
                    : expandedEmbeddedLayout
                      ? { height: 'auto', maxHeight: '100%', overflow: 'auto' }
                      : { height: '100%', overflow: 'auto' }),
                }}
              >
                <div
                  role={tapToExpandPreview ? 'button' : undefined}
                  tabIndex={tapToExpandPreview ? 0 : undefined}
                  aria-label={
                    tapToExpandPreview
                      ? t('organisation.tap-to-expand-preview')
                      : undefined
                  }
                  className={
                    tapToExpandPreview ? 'scope-pwa-document-preview-hit' : undefined
                  }
                  onClick={
                    tapToExpandPreview
                      ? () => setLightboxOpen(true)
                      : undefined
                  }
                  onKeyDown={
                    tapToExpandPreview
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setLightboxOpen(true);
                          }
                        }
                      : undefined
                  }
                  aria-label={
                    tapToExpandPreview
                      ? t('vat-report.tap-to-expand-preview')
                      : undefined
                  }
                  style={{
                    /* Visual footprint after rotation (contain-fitted). */
                    width: scaledImageSize
                      ? imageRotation === 90 || imageRotation === 270
                        ? scaledImageSize.height * previewZoom
                        : scaledImageSize.width * previewZoom
                      : '100%',
                    height: scaledImageSize
                      ? imageRotation === 90 || imageRotation === 270
                        ? scaledImageSize.width * previewZoom
                        : scaledImageSize.height * previewZoom
                      : expandedEmbeddedLayout
                      ? 'auto'
                      : '100%',
                    maxWidth: fitPreviewInPanel ? '100%' : undefined,
                    maxHeight: fitPreviewInPanel ? '100%' : undefined,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'block',
                    flexShrink: 0,
                    cursor: tapToExpandPreview ? 'zoom-in' : undefined,
                  }}
                >
                  <div
                    style={{
                      /* Pre-rotation image box — must NOT use swapped visual size. */
                      width: scaledImageSize
                        ? scaledImageSize.width * previewZoom
                        : undefined,
                      height: scaledImageSize
                        ? scaledImageSize.height * previewZoom
                        : undefined,
                      transform:
                        imageRotation === 90
                          ? 'rotate(90deg) translateY(-100%)'
                          : imageRotation === 180
                          ? 'rotate(180deg) translate(-100%,-100%)'
                          : imageRotation === 270
                          ? 'rotate(270deg) translateX(-100%)'
                          : undefined,
                      transformOrigin: 'top left',
                    }}
                  >
                    <Image
                      key={`preview-${containerWidth}`}
                      src={previewUrl}
                      alt={
                        currentAttachment.fileName ||
                        t('vat-report.attachment-preview')
                      }
                      style={{
                        width: scaledImageSize
                          ? scaledImageSize.width * previewZoom
                          : '100%',
                        height: scaledImageSize
                          ? scaledImageSize.height * previewZoom
                          : 'auto',
                        maxWidth: 'none',
                        maxHeight: 'none',
                        objectFit: 'fill',
                        display: 'block',
                      }}
                      preview={false}
                      onLoad={(e) => {
                        const img = e.target as HTMLImageElement;
                        setImageDimensions({
                          width: img.naturalWidth,
                          height: img.naturalHeight,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : isPdf && previewUrl ? (
              (() => {
                /** Fit-to-viewer width only. A fixed floor (formerly 600) made narrow panels render wider than visible area → clipped PDF + horizontal scroll. */
                const pdfBaseWidth = containerWidth > 0 ? containerWidth : 320;
                const pdfRenderWidth = pdfBaseWidth * previewZoom;

                /** Extra raster density: baseline at 100% zoom + more when zoomed in; outer `zoom` restores layout width (Chromium/Safari). Firefox skips (no zoom). */
                const rasterQualitySupportsCssZoom =
                  typeof window !== 'undefined' &&
                  typeof CSS !== 'undefined' &&
                  typeof CSS.supports === 'function' &&
                  CSS.supports('zoom', '1');
                const zoomQualityFactor =
                  1 +
                  Math.max(0, previewZoom - 1) * PDF_RASTER_ZOOM_QUALITY_SLOPE;
                const rasterQualityBoost = rasterQualitySupportsCssZoom
                  ? Math.min(
                      PDF_RASTER_QUALITY_MAX_BOOST,
                      PDF_RASTER_BASELINE_SHARPNESS * zoomQualityFactor
                    )
                  : 1;
                const pdfRasterWidth = Math.ceil(
                  pdfRenderWidth * rasterQualityBoost
                );
                const pdfRasterZoomWrapStyle =
                  rasterQualityBoost > 1
                    ? ({ zoom: 1 / rasterQualityBoost } as React.CSSProperties)
                    : undefined;

                return (
                  <div
                    role={tapToExpandPreview ? 'button' : undefined}
                    tabIndex={tapToExpandPreview ? 0 : undefined}
                    aria-label={
                      tapToExpandPreview
                        ? t('organisation.tap-to-expand-preview')
                        : undefined
                    }
                    className={
                      tapToExpandPreview
                        ? 'av-pdf-zoom-container scope-pwa-document-preview-hit av-pdf-tap-expand'
                        : 'av-pdf-zoom-container'
                    }
                    onClick={
                      tapToExpandPreview
                        ? () => setLightboxOpen(true)
                        : undefined
                    }
                    onKeyDown={
                      tapToExpandPreview
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setLightboxOpen(true);
                            }
                          }
                        : undefined
                    }
                    aria-label={
                      tapToExpandPreview
                        ? t('vat-report.tap-to-expand-preview')
                        : undefined
                    }
                    style={{
                      width: '100%',
                      cursor: tapToExpandPreview ? 'zoom-in' : undefined,
                      ...(expandedEmbeddedLayout
                        ? { minHeight: 0, overflow: 'visible' }
                        : {
                            height: '100%',
                            overflow: tapToExpandPreview ? 'hidden' : 'auto',
                          }),
                    }}
                  >
                    <div
                      style={{
                        width: pdfRenderWidth,
                        minHeight: expandedEmbeddedLayout ? 'auto' : '100%',
                      }}
                    >
                      <div
                        className="av-pdf-raster-zoom-wrap"
                        style={pdfRasterZoomWrapStyle}
                      >
                        <div
                          style={{
                            width: pdfRasterWidth,
                            minHeight: expandedEmbeddedLayout ? 'auto' : '100%',
                          }}
                        >
                          <Document
                            key={previewUrl}
                            file={previewUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                          >
                            {Array.from(
                              new Array(numPages || 0),
                              (_, index) => (
                                <Page
                                  key={`page_${index + 1}`}
                                  pageNumber={index + 1}
                                  width={pdfRasterWidth}
                                />
                              )
                            )}
                          </Document>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '14px',
                }}
              >
                <p>{t('vat-report.preview-not-available-for-file-type')}</p>
                <p style={{ marginTop: '8px' }}>
                  <a
                    href={previewUrl || '#'}
                    download={currentAttachment.fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!previewUrl) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {t('vat-report.download-to-view')}
                  </a>
                </p>
              </div>
            )}
          </div>
        )}
        {pwaSheetCollapsed && previewUrl && (isImage || isPdf) ? (
          <button
            type="button"
            className="scope-pwa-preview-expand-btn"
            onClick={() => setLightboxOpen(true)}
          >
            {t('vat-report.expand-preview', { defaultValue: 'Expand' })}
          </button>
        ) : null}
      </div>
      {tapToExpandPreview &&
      lightboxOpen &&
      previewUrl &&
      typeof document !== 'undefined'
        ? createPortal(
            <div
              className={`scope-pwa-document-lightbox${
                isPdf ? ' scope-pwa-document-lightbox--pdf-mode' : ''
              }`}
              role="dialog"
              aria-modal="true"
              aria-label={t('vat-report.attachment-preview')}
              onClick={() => setLightboxOpen(false)}
            >
              <button
                type="button"
                className="scope-pwa-document-lightbox__close"
                onClick={() => setLightboxOpen(false)}
                aria-label={t('common.close')}
              >
                ×
              </button>
              {isImage ? (
                <img
                  className="scope-pwa-document-lightbox__img"
                  src={previewUrl}
                  alt={
                    currentAttachment?.fileName ||
                    t('vat-report.attachment-preview')
                  }
                  onClick={(event) => event.stopPropagation()}
                />
              ) : isPdf ? (
                <div
                  className="scope-pwa-document-lightbox__pdf"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Document
                    key={previewUrl}
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                  >
                    {Array.from(new Array(numPages || 0), (_, index) => (
                      <Page
                        key={`lightbox_page_${index + 1}`}
                        pageNumber={index + 1}
                        width={Math.min(
                          typeof window !== 'undefined'
                            ? window.innerWidth - 24
                            : 360,
                          900
                        )}
                      />
                    ))}
                  </Document>
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </AttachmentViewerWrapper>
  );
};
